import { ChangeDetectorRef, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { TdoUserConfigurationModel } from '../../../models/configuration/user/tdo-user-configuration.model';
import { UserService } from '../../../services/user.service';
import { SignalrService } from '../../../services/socket/signalr.service';
import { Globals } from '../../../globals';
import { version, appName } from '../../../../environments/version';
import { AuthenticationResponseModel } from '../model/authentication-response.model';
import { AuthenticationService } from '../services/authentication.service';
import { LoginService } from '../login/login.service';
import { DoctorsService } from '../../../services/doctors.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { TdoImagingService } from '../../../main-interface/imaging/tdo-imaging.service';
import { UiService } from '../../../services/ui.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ResetCredentialsDialogComponent, ResetCredentialsResult } from 'src/app/dialog/reset-credentials-dialog/reset-credentials-dialog.component';
import { finalize } from 'rxjs/operators';
import { TDOAuthenticationResponse } from 'src/app/models/configuration/user/tdo-authentication-response.model';


@Component({
  standalone: false,
  selector: 'app-login-page',
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss']
})
export class LoginPageComponent implements OnInit, OnDestroy {
  private queryParamsSub?: Subscription;
  private returnUrl: string;
  returnParams: any = false;

  appVersion: string = '';
  appName: string = '';
  invalidCredentials: boolean = false;

  public loadingLocations: boolean = true;
  public locations: any[] = [];
  public selectedLocation: string = '';
  public userName: any;
  public password: any;
  public userEmail: any;
  public validated: boolean = false;
  public isLoading: boolean = false;
  public error: string = '';
  public loginMode: boolean = true;
  public passwordResetTokenAvailable: boolean = false;
  public passwordResetToken: string = '';
  public passwordResetConfirmation: boolean = false;
  public showChangeMessage: boolean = false;
  public passwordChange: string = '';
  public passwordConfirmation: string = '';
  public passwordChangeMessages: string[] = [];
  public showPassword: boolean = false;
  public requestResetPasswordMessage: string = '';

  public focusedField: any;
  public mounted = signal(false);
  public useNewLoginPage: boolean = true;
  public loginType: string = '';
  public logoUrl: string = '';

  constructor(private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly globals: Globals,
    private readonly userService: UserService,
    private readonly doctorService: DoctorsService,
    private readonly uiService: UiService,
    private readonly loginService: LoginService,
    private readonly authService: AuthenticationService,
    private readonly localStorageService: LocalStorageService,
    private readonly drawService: TdoImagingService,
    private readonly signalrService: SignalrService,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef) { }

  ngOnInit() {
    this.appVersion = version;
    this.appName = appName;
    this.authService.getLocationLogo().pipe(
      finalize(() => {
        setTimeout(() => {
          this.mounted.set(true); // triggers [class.mounted] in template
        }, 50);
      })
    ).subscribe({
      next: (logoData: any) => {
        this.logoUrl = logoData;
      },
      error: (error) => {
        console.error('error getting location logo: ' + error.message);
        this.logoUrl = '';
      }
    });
    if (this.route.queryParams) {
      this.queryParamsSub?.unsubscribe();
      this.queryParamsSub = this.route.queryParams.subscribe(params => {
        this.passwordResetToken = params['pct'];
        if (this.passwordResetToken) {
          this.passwordResetTokenAvailable = true;
        }
      });
    }

    if (localStorage.getItem('returnURL')) {
      this.returnUrl = localStorage.getItem('returnURL');
      localStorage.removeItem('returnURL');
    } else {
      this.returnUrl = this.route.snapshot.queryParams.returnUrl || '/';
      const lt = this.route.snapshot.queryParams.lt || '';
      if (lt === 'r') {
        this.loginType = 'referring doctor';
      }
    }

    if (localStorage.getItem('returnURLParameters')) {
      this.returnParams = JSON.parse(localStorage.getItem('returnURLParameters'));
      localStorage.removeItem('returnURLParameters');
    }
    // Using setTimeout to trigger change detection after view init
    

    this.getAnnouncements();
    this.loadLocations();
    // get ws mac address from service broker - desktop scanning/TWAIN setup, meaningless for a
    // referring doctor login (no image acquisition capability there), and otherwise retries up
    // to 5 times over ~10s for nothing. Read lt directly rather than relying on this.loginType -
    // when ReferrerAuthGuard redirects here it sets localStorage's returnURL first, which takes
    // the other branch above and never sets this.loginType from the lt query param at all.
    const isReferringDoctorLogin = this.loginType === 'referring doctor' || this.route.snapshot.queryParams.lt === 'r';
    if (!isReferringDoctorLogin) {
      this.isServiceBrokerRunning();
    }
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.queryParamsSub?.unsubscribe();
  }

  getAnnouncements() {
    this.loginService.getAnnouncements()
      .subscribe(response => {
        response.forEach(announcement => {
          if (announcement.MessageType === 'WhatsNew')
            localStorage.setItem('WhatsNew', announcement.Message);
          else
            this.uiService.displayAnnouncement(announcement.MessageType, announcement.MessageTitle, announcement.Message);
        });

      });
  }

  loadLocations() {

    this.loginService.getLocations()
      .subscribe(response => {
        response.forEach(location => {
          location.LOCID = `${location.LOCID}`;
        });
        const locid = localStorage.getItem('LOCID');
        const locationId = locid && locid !== 'null' ? locid : '';
        const locs = response;
        setTimeout(() => {
          this.selectedLocation = locationId;
          this.locations = locs;
          this.loadingLocations = false;
          this.cdr.detectChanges();
        }, 0);
      }, err => {
        setTimeout(() => {
          this.selectedLocation = '';
          this.loadingLocations = false;
          this.cdr.detectChanges();
        }, 0);
        console.error('getLocations error:', err);
        setTimeout(() => { this.loadLocations(); }, 1000);
      });
  }
  isServiceBrokerRunning() {
    let retryCount = 0;
    const maxRetries = 5; // Check up to 5 times
    const retryInterval = 2000; // Wait 2 seconds between retries

    const checkServiceBroker = () => {
      this.drawService.isServiceBrokerRunning(() => {
        const intervalSubscription = setInterval(_ => {
          if (this.drawService.isSBRunning === true) {
            clearInterval(intervalSubscription);
            this.drawService.getWSNameWithMacAddress(() => {
              const intervalSubscription2 = setInterval(_ => {
                if (this.drawService.wsNameWithMACAddress !== '') {
                  clearInterval(intervalSubscription2);
                  this.globals.WorkStationName = this.drawService.wsNameWithMACAddress;
                  console.log('Service Broker is running, workstation name set to:', this.globals.WorkStationName);
                }
              }, 100);
            });
          } else {
            clearInterval(intervalSubscription);
            retryCount++;

            if (retryCount < maxRetries) {
              // console.log(`Service Broker check attempt ${retryCount}/${maxRetries} failed. Retrying in ${retryInterval / 1000} seconds...`);
              setTimeout(() => {
                checkServiceBroker();
              }, retryInterval);
            } else {
              console.log(`Service Broker check failed after ${maxRetries} attempts. Using default workstation name WSTEST`);
              this.globals.WorkStationName = 'WSTEST';
            }
          }
        }, 100);
      });
    };

    // Start the first check
    checkServiceBroker();
  }

  locationSelected(event) {
    localStorage.setItem('LOCID', `${event}`);
  }

  loginUser() {
    this.invalidCredentials = false;
    this.error = '';
    if (this.loginType !== 'referring doctor' && (!this.selectedLocation || this.selectedLocation === 'null')) {
      this.uiService.displayMessage('Warning', 'Location is required');
      return;
    }
    this.isLoading = true;

    this.authService.loginJwt(this.userName, this.password, this.selectedLocation, this.globals.WorkStationName, this.loginType)
      .subscribe({
        next: (dataAuth: AuthenticationResponseModel) => {
          this.isLoading = false;
          if (dataAuth.isSuccess) {
            if (dataAuth.requirePasswordChange) {
              this.resetCredentials(dataAuth.userId);
              return;
            }

            this.processLoginSuccess(dataAuth);
          } else {
            this.error = dataAuth.message || 'Please provide valid credentials.';
            console.error('login error: ', this.error);
            this.invalidCredentials = true;
            this.cdr.detectChanges();
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('error: ' + error.message);
          this.error = this.extractErrorMessage(error);
          this.invalidCredentials = true;
          this.cdr.detectChanges();
        }
      });
  }

  private extractErrorMessage(error: any): string {
    return error?.error?.message ?? error?.message ?? 'Please provide valid credentials.';
  }

  private processLoginSuccess(dataAuth: AuthenticationResponseModel) {
    this.globals.LOCenumCountryId = dataAuth.locationCountryId;
    this.globals.showSuburbInAddress = this.globals.LOCenumCountryId !== '00000007-1000-0000-0000-000000000001'; // show if not  usa
    this.authService.storetokenResult(dataAuth);
    this.validated = true;
    this.setLoggedInUser(dataAuth);
    this.signalrService.connect();

    if (this.loginType === 'referring doctor') {
      this.authService.storeReferringDoctorTokenResult(dataAuth);
      this.router.navigate(['/referrerportal/home']);
    }
    else {
      this.doctorService.checkIfUserIsDoctor(dataAuth.userId).subscribe((res) => {
        if (res) {
          this.localStorageService.loggedInDoctorId = res.DoctorId;
        }
        this.uiService.clearMessages();
        if (this.returnUrl.includes('portal')) 
          this.returnUrl = '';
        if (this.returnParams) {
          this.router.navigate([this.returnUrl], { queryParams: this.returnParams });
        } else {
          this.router.navigate([this.returnUrl]);
        }
      });
    }
  }

  requestResetPassword() {
    this.isLoading = true;
    this.invalidCredentials = false;
    if (this.userName == '') {
      this.isLoading = false;
      this.uiService.displayMessage('Warning', 'Username is required.');
      this.cdr.detectChanges();
      return;
    }
    if (this.userEmail == '') {
      this.uiService.displayMessage('Warning', 'Email is required.');
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    this.authService.resetPasswordRequest(this.userName, this.userEmail, this.globals.WorkStationName, this.loginType)
      .subscribe({
        next: (dataAuth: AuthenticationResponseModel) => {
          this.isLoading = false;
          if (dataAuth.isSuccess) {
            this.requestResetPasswordMessage = 'If user is found, you will receive an email with password reset instructions.';
            this.showChangeMessage = true;
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('error: ' + error.message);
          this.error = this.extractErrorMessage(error);
          this.requestResetPasswordMessage = 'Error requesting password reset. Please try again later.';
          this.showChangeMessage = true;
        }
      });
  }

  resetPassword() {
    this.passwordResetConfirmation = false;
    this.passwordChangeMessages = [];
    this.invalidCredentials = false;

    if (this.passwordChange != this.passwordConfirmation) {
      this.uiService.displayMessage('Warning', 'Passwords do not match.');
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }
    if (this.passwordChange == '') {
      this.uiService.displayMessage('Warning', 'New password is required.');
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }
    if (this.passwordConfirmation == '') {
      this.uiService.displayMessage('Warning', 'Password confirmation is required.');
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }
    this.isLoading = true;
    this.authService.resetPassword(this.passwordResetToken, this.passwordChange, this.passwordConfirmation)
      .subscribe({
        next: (dataAuth: AuthenticationResponseModel) => {
          this.isLoading = false;
          if (dataAuth.isSuccess) {
            this.passwordResetConfirmation = true;
            this.passwordChange = '';
            this.passwordConfirmation = '';
            setTimeout(() => this.router.navigate(['/login']), 2000);
          }
          else {
            this.passwordResetConfirmation = false;
            this.invalidCredentials = true;
            if (dataAuth.message) {
              const msg = dataAuth.message.endsWith('.') ? dataAuth.message.slice(0, -1) : dataAuth.message;
              this.passwordChangeMessages = msg.split('.').map(m => m.trim()).filter(m => m.length > 0);
            } else {
              this.passwordChangeMessages = ['Password change failed. Please verify the reset token and password requirements, then try again.'];
            }
          }

        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('error: ' + error.message);
          this.error = this.extractErrorMessage(error);
          this.passwordResetConfirmation = false;
          this.invalidCredentials = true;
          this.passwordChangeMessages = [error?.error?.message
            ? `Password change failed. ${error.error.message}`
            : 'Password change failed. Please try again later.'];
        }
      });
  }

  private setLoggedInUser(dataAuth: AuthenticationResponseModel) {
    this.userService.loggedInUser = new TdoUserConfigurationModel();
    this.userService.loggedInUser.userid = dataAuth.userId;
    this.userService.loggedInUser.UserID = dataAuth.userId;
    this.userService.loggedInUser.WorkStationID = dataAuth.workStationId;
    this.globals.WorkStationID = dataAuth.workStationId;
    this.userService.loggedInUser.TDOCID = Number(dataAuth.tdoCId);
    this.userService.loggedInUser.UserNickName = dataAuth.userNickName;
  }

  forgotPasswordClicked(): void {
    this.userName = '';
    this.password = '';
    this.userEmail = '';
    this.loginMode = false;
    this.passwordResetTokenAvailable = false;
    this.passwordResetConfirmation = false;
    this.passwordResetToken = '';
    this.passwordChangeMessages = [];
    this.showChangeMessage = false;
    this.requestResetPasswordMessage = '';

  }

  showLogin() {
    this.userName = '';
    this.password = '';
    this.userEmail = '';
    this.passwordChangeMessages = [];
    this.loginMode = true;
    this.passwordResetTokenAvailable = false;
    this.passwordResetConfirmation = false;
    this.showChangeMessage = false;
    this.passwordResetToken = '';
    this.requestResetPasswordMessage = '';
    this.router.navigate(['/login']);
  }

  tdoReferrerClicked(): void {
    this.loginType = 'referring doctor';
    this.invalidCredentials = false;
    this.loginMode = true;
    //this.router.navigateByUrl(`/referrerLogin`);
  }
  tdoUserClicked(): void {
    this.loginType = '';
    this.invalidCredentials = false;
    this.loginMode = true;
    //this.router.navigateByUrl(`/referrerLogin`);
  }
  tdoPatientClicked(): void {
    this.router.navigateByUrl(`/patientLogin`);
  }

  showPasswords() {
    this.showPassword = true;
  }

  hidePasswords() {
    this.showPassword = false;
  }

  resetCredentials(userId: string) {

    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      userName: this.userName,
      userId: userId,
      title: 'Password Reset required by System for: ' + this.userName
    };
    dialogConfig.panelClass = 'dialog';
    dialogConfig.width = '500px';

    const dialogRef = this.dialog.open(ResetCredentialsDialogComponent, dialogConfig);
    const dialogComponent = dialogRef.componentInstance;

    // Subscribe to the confirmClicked event for password generation
    dialogComponent.confirmClicked.subscribe((result: ResetCredentialsResult) => {
      if (result) {
        // Custom password was specified
        this.isLoading = true;
      }

      const resetCredentialsRequest = {
        UserName: this.userName,
        UserId: userId,
        Password: result.Password,
        RepeatedPassword: result.RepeatedPassword
      };

      this.loginService.resetUserCredentials(resetCredentialsRequest).pipe(
        finalize(() => {
          this.isLoading = false;
        })).subscribe((response: TDOAuthenticationResponse) => {
          if (response && response.Success) {
            // Display success message in dialog, using Message if available
            const successMessage = response.Message || 'User credentials changed successfully, you can now login with your new password.';
            dialogComponent.setSuccessMessage(successMessage);
            // Update doctor data

          } else {
            // Handle error case - display in dialog
            const errorMessage = response?.Message || 'An unknown error occurred while resetting credentials.';
            dialogComponent.setErrorMessage(errorMessage);
          }
        },
          err => {
            console.error(err);
            const errorMessage = err.error?.ExceptionMessage ?? err.error?.Message ?? err.error ?? 'An unknown error occurred while resetting credentials.';
            dialogComponent.setErrorMessage(`Error modifying credentials: ${errorMessage}`);
          });

    });
  }
}
